/*
 * Code generated by Speakeasy (https://speakeasy.com). DO NOT EDIT.
 */

import {
  MutationKey,
  useMutation,
  UseMutationResult,
} from "@tanstack/react-query";
import { GramCore } from "../core.js";
import { spendRulesRulesArchive } from "../funcs/spendRulesRulesArchive.js";
import { combineSignals } from "../lib/primitives.js";
import { RequestOptions } from "../lib/sdks.js";
import { GramError } from "../models/errors/gramerror.js";
import {
  ConnectionError,
  InvalidRequestError,
  RequestAbortedError,
  RequestTimeoutError,
  UnexpectedClientError,
} from "../models/errors/httpclienterrors.js";
import { ResponseValidationError } from "../models/errors/responsevalidationerror.js";
import { SDKValidationError } from "../models/errors/sdkvalidationerror.js";
import { ServiceError } from "../models/errors/serviceerror.js";
import {
  ArchiveSpendRuleRequest,
  ArchiveSpendRuleSecurity,
} from "../models/operations/archivespendrule.js";
import { unwrapAsync } from "../types/fp.js";
import { useGramContext } from "./_context.js";
import { MutationHookOptions } from "./_types.js";

export type SpendRulesArchiveRuleMutationVariables = {
  request: ArchiveSpendRuleRequest;
  security?: ArchiveSpendRuleSecurity | undefined;
  options?: RequestOptions;
};

export type SpendRulesArchiveRuleMutationData = void;

export type SpendRulesArchiveRuleMutationError =
  | ServiceError
  | GramError
  | ResponseValidationError
  | ConnectionError
  | RequestAbortedError
  | RequestTimeoutError
  | InvalidRequestError
  | UnexpectedClientError
  | SDKValidationError;

/**
 * archiveSpendRule spendRules
 *
 * @remarks
 * Archive a budget rule. There is no delete: archiving ends the rule's lineage while retaining its slug, version history, and events. Any open circuits for the rule close on the next evaluation cycle.
 */
export function useSpendRulesArchiveRuleMutation(
  options?: MutationHookOptions<
    SpendRulesArchiveRuleMutationData,
    SpendRulesArchiveRuleMutationError,
    SpendRulesArchiveRuleMutationVariables
  >,
): UseMutationResult<
  SpendRulesArchiveRuleMutationData,
  SpendRulesArchiveRuleMutationError,
  SpendRulesArchiveRuleMutationVariables
> {
  const client = useGramContext();
  return useMutation({
    ...buildSpendRulesArchiveRuleMutation(client, options),
    ...options,
  });
}

export function mutationKeySpendRulesArchiveRule(): MutationKey {
  return ["@gram/client", "rules", "archive"];
}

export function buildSpendRulesArchiveRuleMutation(
  client$: GramCore,
  hookOptions?: RequestOptions,
): {
  mutationKey: MutationKey;
  mutationFn: (
    variables: SpendRulesArchiveRuleMutationVariables,
  ) => Promise<SpendRulesArchiveRuleMutationData>;
} {
  return {
    mutationKey: mutationKeySpendRulesArchiveRule(),
    mutationFn: function spendRulesArchiveRuleMutationFn({
      request,
      security,
      options,
    }): Promise<SpendRulesArchiveRuleMutationData> {
      const mergedOptions = {
        ...hookOptions,
        ...options,
        fetchOptions: {
          ...hookOptions?.fetchOptions,
          ...options?.fetchOptions,
          signal: combineSignals(
            hookOptions?.fetchOptions?.signal,
            options?.fetchOptions?.signal,
          ),
        },
      };
      return unwrapAsync(spendRulesRulesArchive(
        client$,
        request,
        security,
        mergedOptions,
      ));
    },
  };
}
